import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import HeroBanner from '../components/HeroBanner';
import { FiBook, FiAward, FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiShoppingCart } from 'react-icons/fi';

const LandingPage = () => {
  const { user } = useAuth();
  const [popularCourses, setPopularCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const API_BASE_URL = 'http://localhost/recc';
  const isLoggedIn = !!user;

  const stats = settings?.stats || {
    total_students: 1234,
    total_courses: 50,
    success_rate: 95,
  };

  useEffect(() => {
    fetchSiteSettings();
    fetchPopularCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/site-settings`);
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchPopularCourses = async () => {
    try {
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const response = await fetch(`${API_BASE_URL}/api/popularcourse?email=${email}&type=popular`);
      const data = await response.json();
      
      if (data.success) {
        setPopularCourses(data.data.slice(0, 6)); // Show only 6 courses
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: FiBook,
      title: 'Quality Content',
      description: 'Comprehensive study materials prepared by expert educators',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: FiAward,
      title: 'Expert Teachers',
      description: 'Learn from experienced faculty with proven track records',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: FiUsers,
      title: 'Test Series',
      description: 'Practice with AI-powered quizzes and mock tests',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: FiTrendingUp,
      title: 'Track Progress',
      description: 'Monitor your performance with detailed analytics',
      color: 'bg-orange-100 text-orange-600'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      {/* Hero Banner */}
      <HeroBanner />

      {/* Welcome Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Welcome to Raj Economics (RECC)
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Your Gateway to Success in Competitive Exams
            </p>
            <p className="text-gray-600 leading-relaxed">
              We provide quality education and comprehensive study materials for students aspiring to crack competitive exams. Join thousands of successful students who achieved their dreams with us.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
                {stats.total_students}+
              </div>
              <div className="text-gray-600">Happy Students</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
                {stats.total_courses}+
              </div>
              <div className="text-gray-600">Quality Courses</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-600 mb-2">
                {stats.success_rate}%
              </div>
              <div className="text-gray-600">Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us?
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to succeed in one place
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card hover:shadow-xl transition-all">
                  <div className={`w-16 h-16 ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Courses Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                🔥 Popular Courses
              </h2>
              <p className="text-xl text-gray-600">
                Most loved courses by our students
              </p>
            </div>
            <Link
              to="/courses"
              className="hidden md:inline-block px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              View All Courses →
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                  <div className="bg-gray-200 h-6 rounded mb-2"></div>
                  <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularCourses.map((course) => {
                const finalPrice = (course.price - (course.price * course.discount / 100)).toFixed(2);
                const isPurchased = course.purchased > 0;

                return (
                  <Link
                    key={course.id}
                    to={`/course-details/${course.id}`}
                    className="card hover:shadow-xl transition-all group"
                  >
                    {/* Course Image */}
                    <div className="relative mb-4 rounded-lg overflow-hidden">
                      <img
                        src={`${API_BASE_URL}/${course.course_thumbnail}`}
                        alt={course.title}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3C/svg%3E';
                        }}
                      />
                      {course.discount > 0 && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                          {course.discount}% OFF
                        </div>
                      )}
                      {isPurchased && (
                        <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <FiCheckCircle className="w-4 h-4" />
                          OWNED
                        </div>
                      )}
                    </div>

                    {/* Course Info */}
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <FiBook className="w-4 h-4" />
                        {course.tradeName || 'General'}
                      </span>
                      {course.validity && (
                        <span className="flex items-center gap-1">
                          <FiClock className="w-4 h-4" />
                          {course.validity} days
                        </span>
                      )}
                    </div>

                    {/* Price */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-primary-600">
                          ₹{finalPrice}
                        </span>
                        {course.discount > 0 && (
                          <span className="text-sm text-gray-500 line-through ml-2">
                            ₹{course.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {!isPurchased && (
                        <FiShoppingCart className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="text-center mt-8 md:hidden">
            <Link
              to="/courses"
              className="btn-primary inline-block"
            >
              View All Courses →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of successful students today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/courses"
              className="px-8 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:shadow-xl transition-all transform hover:scale-105"
            >
              Browse Courses
            </Link>
            {!isLoggedIn && (
              <Link
                to="/register"
                className="px-8 py-3 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all"
              >
                Register Now
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;

